import { ResponsiveData, Section, DeviceData } from "@/types/models/page";
import {
  ComponentConfig,
  ComponentDefinition,
} from "@/types/models/componentConfig";
import {
  normalizeResponsiveBucket,
  setResponsiveBucketValue,
  type ResponsiveBucket,
} from "@team-shiphouse/shiphouse-sdk/shared";

export type DeviceType = "desktop" | "tablet" | "mobile";

export interface ChangeHistoryEntry {
  fieldKey: string;
  oldValue: any;
  newValue: any;
  device: DeviceType;
  timestamp: string;
}

class SectionDataService {
  private changeHistory = new Map<string, ChangeHistoryEntry[]>();
  private maxHistorySize = 100;

  private deepClone<T>(obj: T): T {
    if (obj === null || typeof obj !== "object") {
      return obj;
    }
    return JSON.parse(JSON.stringify(obj));
  }

  private getComponentDataMap(
    section: Section,
  ): Record<string, ResponsiveData> {
    if (!section.component_data) {
      return {};
    }
    if (Array.isArray(section.component_data)) {
      return this.convertArrayToMap(section.component_data as any[]);
    }
    return section.component_data;
  }

  private convertArrayToMap(arrayData: any[]): Record<string, ResponsiveData> {
    const map: Record<string, ResponsiveData> = {};
    arrayData.forEach((item, index) => {
      // Try different possible key sources
      const key =
        item.key || item.name || item.componentSlug || `component-${index}`;
      if (key) {
        map[key] = item;
      }
    });
    return map;
  }

  private createComponentMapping(section: Section): Map<string, string[]> {
    const mapping = new Map<string, string[]>();
    const componentData = this.getComponentDataMap(section);

    for (const dataKey in componentData) {
      const slug = componentData[dataKey]?.componentSlug;
      if (slug) {
        if (!mapping.has(slug)) {
          mapping.set(slug, []);
        }
        mapping.get(slug)!.push(dataKey);
      }
    }
    return mapping;
  }

  private createFieldKey(componentSlug: string, configTitle: string): string {
    return `${componentSlug}::${configTitle}`;
  }

  private getConfigInputKey(config: ComponentConfig): string {
    return config.input_key || config.title;
  }

  private parseFieldKey(fieldKey: string): {
    componentSlug: string;
    configTitle: string;
  } {
    const parts = fieldKey.split("::");
    return { componentSlug: parts[0], configTitle: parts[1] };
  }

  private getDeviceData(
    component: ResponsiveData,
    device: DeviceType,
  ): DeviceData {
    switch (device) {
      case "desktop":
        return component.desktop;
      case "tablet":
        return component.tablet;
      case "mobile":
        return component.mobile;
    }
  }

  private getInputValueFromBucket(bucket: DeviceData, inputKey: string): any {
    if (Array.isArray(bucket)) {
      const item = bucket.find((entry: any) => entry.inputKey === inputKey);
      return item?.value;
    }

    if (bucket && typeof bucket === "object") {
      return (bucket as Record<string, any>)[inputKey];
    }

    return undefined;
  }

  private getInputTypeFromBucket(
    bucket: DeviceData,
    inputKey: string,
  ): string | undefined {
    if (Array.isArray(bucket)) {
      const item = bucket.find((entry: any) => entry.inputKey === inputKey);
      return typeof item?.type === "string" ? item.type : undefined;
    }

    return undefined;
  }

  private getBucketEntries(bucket: DeviceData): Array<{
    inputKey: string;
    type?: string;
    value: any;
  }> {
    if (Array.isArray(bucket)) {
      return bucket
        .filter((entry: any) => entry && typeof entry.inputKey === "string")
        .map((entry: any) => ({
          inputKey: entry.inputKey,
          type: typeof entry.type === "string" ? entry.type : undefined,
          value: entry.value,
        }));
    }

    if (bucket && typeof bucket === "object") {
      return Object.entries(bucket as Record<string, any>).map(([inputKey, value]) => ({
        inputKey,
        value,
      }));
    }

    return [];
  }

  private stableStringify(value: unknown): string {
    const seen = new WeakSet<object>();

    const normalize = (input: unknown): unknown => {
      if (input === null || typeof input !== "object") {
        return input;
      }

      if (seen.has(input as object)) {
        return "[Circular]";
      }

      seen.add(input as object);

      if (Array.isArray(input)) {
        return input.map((item) => normalize(item));
      }

      const entries = Object.entries(input as Record<string, unknown>).sort(
        ([left], [right]) => left.localeCompare(right),
      );

      return entries.reduce<Record<string, unknown>>((acc, [key, entry]) => {
        acc[key] = normalize(entry);
        return acc;
      }, {});
    };

    try {
      return JSON.stringify(normalize(value));
    } catch {
      return String(value);
    }
  }

  private valuesAreEqual(left: unknown, right: unknown): boolean {
    if (left === right) {
      return true;
    }

    if (
      typeof left === "number" &&
      typeof right === "number" &&
      Number.isNaN(left) &&
      Number.isNaN(right)
    ) {
      return true;
    }

    return this.stableStringify(left) === this.stableStringify(right);
  }

  private setInputValueInBucket(
    bucket: DeviceData,
    inputKey: string,
    value: any,
    inputType: string,
  ): DeviceData {
    return setResponsiveBucketValue(
      normalizeResponsiveBucket(bucket as ResponsiveBucket),
      inputKey,
      value,
      inputType,
    ) as DeviceData;
  }

  private removeInputValueFromBucket(
    bucket: DeviceData,
    inputKey: string,
  ): DeviceData {
    if (Array.isArray(bucket)) {
      return bucket.filter((entry: any) => entry.inputKey !== inputKey);
    }

    if (bucket && typeof bucket === "object") {
      const next = { ...(bucket as Record<string, any>) };
      delete next[inputKey];
      return next;
    }

    return bucket;
  }

  private getDefaultValueForConfig(configItem: ComponentConfig): any {
    if (configItem.default_value !== undefined) {
      return this.deepClone(configItem.default_value);
    }

    switch (configItem.type) {
      case "checkbox":
        return false;
      case "color":
        return "#000000";
      case "select":
        return configItem.accepted_values?.[0] || "";
      case "image":
      case "video":
        return "";
      case "multiimage":
      case "multivideo":
      case "text-list":
      case "link-list":
      case "image-list":
      case "icon-list":
      case "feature-card-list":
      case "testimonial-list":
      case "faq-list":
      case "accordion-list":
      case "tab-list":
      case "media-gallery":
      case "promo-card-list":
        return [];
      case "product-selector":
        return { enabled: true };
      case "collection-selector":
      case "category-selector":
      case "product-metafield-binding":
      case "collection-metafield-binding":
      case "section-style-config":
      case "card-image-style-config":
      case "card-title-style-config":
      case "card-cta-style-config":
      case "card-style-config":
      case "style-config":
      case "swiper-config":
        return {};
      case "asset-selector":
      case "navigation-menu":
      case "social-links-config":
      case "spacing-config":
      case "typography-config":
      case "grid-config":
      case "animation-config":
      case "seo-config":
        return {};
      case "customjs":
      case "customcss":
      case "text":
      default:
        return "";
    }
  }

  /**
   * Get the effective value for a device by applying the diff-based structure
   * Desktop contains all values, tablet = desktop + tablet changes, mobile = tablet + mobile changes
   */

  private getEffectiveValueForDevice(
    component: ResponsiveData,
    device: DeviceType,
    inputKey: string,
  ): any {
    const desktopValue = this.getInputValueFromBucket(component.desktop, inputKey);
    if (device === "desktop") {
      return desktopValue;
    }

    const tabletValue = this.getInputValueFromBucket(component.tablet, inputKey);
    if (device === "tablet") {
      return tabletValue !== undefined ? tabletValue : desktopValue;
    }

    const mobileValue = this.getInputValueFromBucket(component.mobile, inputKey);
    return mobileValue !== undefined
      ? mobileValue
      : tabletValue !== undefined
        ? tabletValue
        : desktopValue;
  }

  public initializeFormValuesWithMapping(
    section: Section,
    componentDefinitions: ComponentDefinition[],
    device: DeviceType,
    componentKey?: string, // Optional: if provided, only initialize for this specific component
  ): Record<string, any> {
    const formValues: Record<string, any> = {};
    const componentData = this.getComponentDataMap(section);
    const mapping = this.createComponentMapping(section);

    // If componentKey is provided, only initialize for that specific component
    if (componentKey && componentData[componentKey]) {
      const component = componentData[componentKey];
      const definition = componentDefinitions.find(
        (def) => def.slug === component.componentSlug,
      );
      const deviceBucket = this.getDeviceData(component, device);
      const bucketEntries = this.getBucketEntries(deviceBucket);

      bucketEntries.forEach((entry) => {
        const fieldKey = this.createFieldKey(
          component.componentSlug || definition?.slug || "component",
          entry.inputKey,
        );
        formValues[fieldKey] = this.deepClone(entry.value);
      });

      if (definition) {
        for (const config of definition.config) {
          const inputKey = this.getConfigInputKey(config);
          const fieldKey = this.createFieldKey(definition.slug, inputKey);

          if (formValues[fieldKey] !== undefined) {
            continue;
          }

          const value = this.getEffectiveValueForDevice(
            component,
            device,
            inputKey,
          );

          formValues[fieldKey] = this.deepClone(
            value !== undefined ? value : this.getDefaultValueForConfig(config),
          );
        }
      }
      return formValues;
    }

    // Original behavior: initialize for all components (fallback for backward compatibility)
    for (const definition of componentDefinitions) {
      const mappedKeys = mapping.get(definition.slug) || [];
      if (mappedKeys.length === 0) continue;

      const dataKey = mappedKeys[0];
      const component = componentData[dataKey];
      if (!component) continue;
      const deviceBucket = this.getDeviceData(component, device);
      const bucketEntries = this.getBucketEntries(deviceBucket);

      bucketEntries.forEach((entry) => {
        const fieldKey = this.createFieldKey(
          definition.slug,
          entry.inputKey,
        );
        formValues[fieldKey] = this.deepClone(entry.value);
      });

      for (const config of definition.config) {
        const inputKey = this.getConfigInputKey(config);
        const fieldKey = this.createFieldKey(definition.slug, inputKey);

        if (formValues[fieldKey] !== undefined) {
          continue;
        }

        const value = this.getEffectiveValueForDevice(
          component,
          device,
          inputKey,
        );

        formValues[fieldKey] = this.deepClone(
          value !== undefined ? value : this.getDefaultValueForConfig(config),
        );
      }
    }
    return formValues;
  }

  public updateFormValueWithMapping(
    section: Section,
    componentDefinitions: ComponentDefinition[],
    fieldKey: string,
    value: any,
    device: DeviceType,
    componentKey?: string, // Add optional componentKey parameter to specify which component to update
  ): Section {
    console.log("sectionDataService.updateFormValueWithMapping called:", {
      sectionId: section.id,
      fieldKey,
      value,
      device,
      componentKey,
      originalComponentData: section.component_data,
    });

    const { componentSlug, configTitle } = this.parseFieldKey(fieldKey);

    const newSection = this.deepClone(section);
    const componentData = this.getComponentDataMap(newSection);

    console.log("After deepClone and getComponentDataMap:", {
      newSectionId: newSection.id,
      componentDataKeys: Object.keys(componentData),
      componentData,
    });

    const mapping = this.createComponentMapping(newSection);

    let dataKey: string;

    // If componentKey is provided, use it directly (fixes the bug where multiple components share the same slug)
    if (componentKey && componentData[componentKey]) {
      dataKey = componentKey;
    } else {
      // Fallback to the old behavior for backward compatibility
      let mappedKeys = mapping.get(componentSlug);

      if (!mappedKeys || mappedKeys.length === 0) {
        const fallbackKey = `${componentSlug}-1`;
        mappedKeys = [fallbackKey];
        componentData[fallbackKey] = {
          desktop: [],
          tablet: [],
          mobile: [],
          name: fallbackKey,
          sectionID: newSection.id,
          type: "component",
          componentSlug,
        };
      }

      dataKey = mappedKeys[0];
    }

    const component = componentData[dataKey];

    if (!component) {
      console.warn("Component not found for dataKey:", dataKey);
      return newSection;
    }

    const definition = componentDefinitions.find(
      (def) => def.slug === componentSlug,
    );
    const config = definition?.config.find(
      (c) => this.getConfigInputKey(c) === configTitle || c.title === configTitle,
    );
    let oldValue: any;

    // Get the current value before updating
    oldValue = this.getEffectiveValueForDevice(component, device, configTitle);
    const existingInputType =
      this.getInputTypeFromBucket(component[device], configTitle) ||
      this.getInputTypeFromBucket(component.desktop, configTitle) ||
      this.getInputTypeFromBucket(component.tablet, configTitle) ||
      this.getInputTypeFromBucket(component.mobile, configTitle) ||
      config?.type ||
      "text";

    console.log("Before update:", {
      component,
      oldValue,
      configTitle,
      device,
    });

    // Update the value in the current device, preserving the existing bucket shape
    component[device] = this.setInputValueInBucket(
      component[device],
      configTitle,
      this.deepClone(value),
      existingInputType,
    );

    // If this is tablet or mobile, we need to check if the value is different from the base
    // If it's the same as the base, we can remove it from the diff array
    if (device === "tablet" || device === "mobile") {
      const baseDevice = device === "tablet" ? "desktop" : "tablet";
      const baseValue = this.getEffectiveValueForDevice(
        component,
        baseDevice,
        configTitle,
      );

      if (this.valuesAreEqual(value, baseValue)) {
        component[device] = this.removeInputValueFromBucket(
          component[device],
          configTitle,
        );
      }
    }

    if (oldValue === undefined) {
      oldValue = config ? this.getDefaultValueForConfig(config) : undefined;
    }

    this.addToChangeHistory(newSection.id, {
      fieldKey,
      oldValue,
      newValue: value,
      device,
      timestamp: new Date().toISOString(),
    });

    // Ensure we create a new reference for component_data to trigger Redux updates
    newSection.component_data = { ...componentData };

    console.log("After update:", {
      updatedComponentData: newSection.component_data,
      componentDataKeys: Object.keys(newSection.component_data),
    });

    return newSection;
  }

  private addToChangeHistory(sectionId: string, change: ChangeHistoryEntry) {
    if (!this.changeHistory.has(sectionId)) {
      this.changeHistory.set(sectionId, []);
    }
    const history = this.changeHistory.get(sectionId)!;
    history.push(change);
    if (history.length > this.maxHistorySize) {
      history.shift();
    }
  }

  public getChangeHistory(sectionId: string): ChangeHistoryEntry[] {
    return this.changeHistory.get(sectionId) || [];
  }

  public clearChangeHistory(sectionId: string) {
    this.changeHistory.delete(sectionId);
  }
}

export const sectionDataService = new SectionDataService();
